/**
 * R6 — Verify residency (design/redesign_v3/Auth & Onboarding.dc.html frames
 * "Verify" + "Onboarding desktop"). Role radio list (Owner/Tenant/Family),
 * document upload placeholder (no upload API yet), encryption note. On
 * submit, routes to /onboarding/signup passing societyId + flatConfigId +
 * flatCode + residentType. Desktop (≥1024): steps rail + centered content.
 */
import { router, useLocalSearchParams } from 'expo-router';
import React from 'react';
import { Pressable, View, useWindowDimensions } from 'react-native';

import { radius, space } from '@bero/tokens';
import { InfoNote } from '@/components/gate/bits';
import { Icon } from '@/features/account-admin/icons';
import { BottomBar } from '@/features/community/bits';
import { useT } from '@/i18n';
import type { DictKey } from '@/i18n/en';
import { useTheme } from '@/theme';
import { AppText, Button, EmptyState, Screen, TopBar } from '@/ui';

import { DesktopShell, ONBOARDING_DESKTOP_BREAKPOINT, StepsRail } from './_shell';

const ROLE_OPTIONS = [
  { code: 'OWNER', labelKey: 'onb_role_owner', subKey: 'onb_role_owner_sub' },
  { code: 'TENANT', labelKey: 'onb_role_tenant', subKey: 'onb_role_tenant_sub' },
  { code: 'FAMILY', labelKey: 'onb_role_family', subKey: 'onb_role_family_sub' },
] as const satisfies readonly { code: 'OWNER' | 'TENANT' | 'FAMILY'; labelKey: DictKey; subKey: DictKey }[];
type RoleCode = (typeof ROLE_OPTIONS)[number]['code'];

/** Radio row list — port of the frame's role picker (circle + label + sub,
 * bordered/tinted when selected). */
function RoleRow({ label, sub, selected, onPress }: { label: string; sub: string; selected: boolean; onPress: () => void }) {
  const { colors } = useTheme();
  return (
    <Pressable
      accessibilityRole="radio"
      accessibilityState={{ selected }}
      onPress={onPress}
      style={{
        flexDirection: 'row', alignItems: 'center', gap: space.s3,
        padding: space.s4, borderRadius: radius.lg, borderWidth: 1, minHeight: 44,
        borderColor: selected ? colors.primary500 : colors.borderSoft,
        backgroundColor: selected ? colors.primary50 : colors.canvas0,
      }}
    >
      <View style={{
        width: 20, height: 20, borderRadius: radius.full, borderWidth: 2,
        borderColor: selected ? colors.primary500 : colors.borderFirm,
        alignItems: 'center', justifyContent: 'center',
      }}>
        {selected ? <View style={{ width: 10, height: 10, borderRadius: radius.full, backgroundColor: colors.primary500 }} /> : null}
      </View>
      <View style={{ flex: 1 }}>
        <AppText variant="headingSm" weight="600">{label}</AppText>
        <AppText variant="bodySm" tone="tertiary" style={{ marginTop: 2 }}>{sub}</AppText>
      </View>
    </Pressable>
  );
}

function UploadArea({ onPress }: { onPress: () => void }) {
  const { colors } = useTheme();
  const { t } = useT();
  return (
    <Pressable
      accessibilityRole="button"
      onPress={onPress}
      style={{
        borderWidth: 1.5, borderStyle: 'dashed', borderColor: colors.borderFirm,
        backgroundColor: colors.canvas1, borderRadius: radius.xl,
        paddingVertical: space.s8, paddingHorizontal: space.s4,
        alignItems: 'center', minHeight: 44,
      }}
    >
      <Icon name="file-text" size={32} color={colors.textTertiary} />
      <AppText variant="bodyMd" weight="500" style={{ marginTop: space.s3, textAlign: 'center' }}>
        {t('onb_verify_upload_title')}
      </AppText>
      <AppText variant="bodySm" tone="tertiary" style={{ marginTop: 4 }}>{t('onb_verify_upload_hint')}</AppText>
    </Pressable>
  );
}

export default function OnboardingVerifyScreen() {
  const { colors } = useTheme();
  const { t } = useT();
  const { width } = useWindowDimensions();
  const { societyId, flatConfigId, flatCode, societyName } = useLocalSearchParams<{
    societyId?: string; flatConfigId?: string; flatCode?: string; societyName?: string;
  }>();

  const [role, setRole] = React.useState<RoleCode>('OWNER');
  const [notice, setNotice] = React.useState<string | null>(null);

  if (!societyId || !flatConfigId) {
    return (
      <Screen scroll={false}>
        <TopBar title="" onBack={() => router.back()} bordered={false} />
        <EmptyState
          icon={<Icon name="alert-circle" size={40} color={colors.textTertiary} />}
          title={t('onb_start_over_title')}
          body={t('onb_verify_start_over_body')}
          cta={t('onb_choose_society')}
          onCta={() => router.replace('/onboarding/society')}
        />
      </Screen>
    );
  }

  const submit = () => {
    const qs = new URLSearchParams({
      societyId, flatConfigId, residentType: role,
    });
    if (flatCode) qs.set('flatCode', flatCode);
    if (societyName) qs.set('societyName', societyName);
    router.push(`/onboarding/signup?${qs.toString()}`);
  };

  const breadcrumb = [flatCode, societyName].filter(Boolean).join(' · ');

  const body = (
    <>
      <View>
        <AppText variant="bodySm" weight="600" tone="tertiary" style={{ textTransform: 'uppercase', letterSpacing: 0.6 }}>
          {t('onb_verify_i_am')}
        </AppText>
        <View style={{ gap: space.s3, marginTop: space.s2 }}>
          {ROLE_OPTIONS.map((r) => (
            <RoleRow key={r.code} label={t(r.labelKey)} sub={t(r.subKey)} selected={role === r.code} onPress={() => setRole(r.code)} />
          ))}
        </View>
      </View>

      <View style={{ marginTop: space.s6 }}>
        <AppText variant="bodySm" weight="600" tone="tertiary" style={{ textTransform: 'uppercase', letterSpacing: 0.6, marginBottom: space.s2 }}>
          {t('onb_verify_proof')}
        </AppText>
        <UploadArea onPress={() => setNotice(t('onb_verify_upload_unavailable'))} />
      </View>

      <InfoNote icon="shield" tint={colors.primary500} style={{ marginTop: space.s4 }}>
        {t('onb_verify_encrypt_note')}
      </InfoNote>

      {notice ? <InfoNote icon="info" style={{ marginTop: space.s3 }}>{notice}</InfoNote> : null}
    </>
  );

  if (width >= ONBOARDING_DESKTOP_BREAKPOINT) {
    return (
      <DesktopShell
        rail={<StepsRail steps={[
          { label: t('onb_step_society'), sub: societyName ?? t('onb_step_society_chosen'), state: 'done' },
          { label: t('onb_step_flat'), sub: flatCode ?? t('onb_step_flat_chosen'), state: 'done' },
          { label: t('onb_step_verify'), sub: t('onb_step_verify_sub'), state: 'active' },
        ]} />}
      >
        <View style={{ maxWidth: 560, width: '100%', flex: 1 }}>
          <AppText variant="displayLg">{t('onb_verify_title')}</AppText>
          <AppText variant="bodyMd" tone="secondary" style={{ marginTop: space.s2 }}>
            {(breadcrumb ? `${breadcrumb} · ` : '') + t('onb_verify_reviewed')}
          </AppText>
          <View style={{ marginTop: space.s6 }}>{body}</View>
          <View style={{ flexDirection: 'row', justifyContent: 'flex-end', gap: space.s3, marginTop: 'auto', paddingTop: space.s8 }}>
            <Button title={t('onb_back')} kind="secondary" onPress={() => router.back()} />
            <Button title={t('onb_verify_submit')} onPress={submit} />
          </View>
        </View>
      </DesktopShell>
    );
  }

  return (
    <Screen scroll={false}>
      <TopBar title="" onBack={() => router.back()} right={breadcrumb ? <AppText variant="bodySm" tone="tertiary">{breadcrumb}</AppText> : undefined} bordered={false} />
      <Screen padded style={{ paddingTop: space.s2, gap: 0 }}>
        <View>
          <AppText variant="headingLg">{t('onb_verify_title')}</AppText>
          <AppText variant="bodyMd" tone="secondary" style={{ marginTop: space.s2 }}>
            {t('onb_verify_sub_mobile')}
          </AppText>
        </View>
        <View style={{ marginTop: space.s6 }}>{body}</View>
      </Screen>
      <BottomBar>
        <Button title={t('onb_verify_submit')} size="lg" onPress={submit} />
      </BottomBar>
    </Screen>
  );
}
